import {
  modelCommand,
  modelOffer,
  useModelStatus,
  type ModelAction,
} from "./model-download.js";
import { useScrollMode } from "./scroll-link.js";

/**
 * The first-visit download, on screen (web ticket 15).
 *
 * The claim this component exists to make is that cuekards is **never worse than a
 * conventional teleprompter** (goal G5, story 23). That is why it is a panel on
 * the setup screen and not a gate in front of it: the boxes below it work, the
 * prompter works, and this is an upgrade offered beside them. A visitor who never
 * presses the button gets a working teleprompter and an honest sentence about why
 * it is not following their voice — which is the whole difference between a
 * product that degrades and one that breaks.
 *
 * Every phase draws from {@link modelOffer}, so this component decides *layout*
 * and the copy is decided once for both places it is drawn. What is here that is
 * not there is the bar, because a bar is not a sentence.
 *
 * **Since flow ticket 06 it no longer makes the first offer.** `Follow my voice`
 * above it does, and this is what happens once somebody has said yes: the bar,
 * the failure, and the two ways out of one.
 *
 * So `needed` is skipped *only while the box is off*, and the condition is the
 * whole point rather than a detail. Off, the offer belongs to the box and a panel
 * repeating it underneath would be the same offer made twice. On, `needed` is not
 * an offer at all — it is a download that was cancelled (story 20's "changed my
 * mind") or a cache the browser evicted, and it is the one state where somebody
 * who has already asked for this has nothing else on screen to press. Skipping it
 * there would leave the feature switched on, nothing downloading, and no way back
 * except unticking the box and ticking it again.
 *
 * `unavailable` is skipped outright, and for the plainer reason: the box says it
 * now. Its sentence and this panel's are the same string out of the same
 * function, so drawing both puts one sentence on screen twice, an inch apart —
 * and the one that belongs is the one beside the dead switch it explains.
 *
 * Both filtered here rather than inside {@link modelOffer}, because the sentences
 * those phases return are the ones the box and its dialog quote: a filter in the
 * decision would put the copy in one place and its readers on opposite sides of
 * it. `ModelChip` still draws every phase and is still unmounted.
 */
export function ModelPanel(): JSX.Element | null {
  const status = useModelStatus();
  const asked = useScrollMode() === "speech";
  if (status.phase === "unavailable") return null;
  if (status.phase === "needed" && !asked) return null;
  const offer = modelOffer(status);
  if (offer === null) return null;

  return (
    <section
      className={`web-model phase-${status.phase}`}
      // `status` and not `alert`: a download panel is a running commentary, and
      // an assertive live region would interrupt a screen reader every time the
      // bar moved. The one phase worth interrupting for is a failure.
      role={status.phase === "failed" ? "alert" : "status"}
    >
      <p className="web-model-says">{offer.says}</p>
      {offer.progress !== undefined && (
        <div
          className="web-model-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.floor(offer.progress * 100)}
        >
          <span style={{ width: `${offer.progress * 100}%` }} />
        </div>
      )}
      <div className="web-model-actions">
        <Press action={offer.action} className="web-model-go" />
        <Press action={offer.also} className="web-model-also" />
      </div>
    </section>
  );
}

/**
 * The same thing, in the prompter's chrome.
 *
 * Somebody who is already in front of the camera and has noticed the prompter is
 * not following needs two things: why, and the one control that changes it. They
 * do not need "Remove the model", which is why {@link ModelOffer.also} is drawn
 * on the panel and not here — a button that deletes sixty megabytes, in a strip
 * of buttons somebody is reaching into mid-take, is a mistake waiting to be made.
 *
 * Silent when the model is ready or being looked for, because the chrome already
 * has a follower state beside it and a second opinion about the same thing is how
 * two lines start disagreeing. That silence is {@link modelOffer}'s and not this
 * component's, so it is the same silence the panel keeps — and it is what
 * {@link FollowControls} fills with the follower's own error, on the one path
 * where a recogniser fails for a reason that has nothing to do with the model.
 */
export function ModelChip({
  /**
   * What to say instead when the model has nothing to say.
   *
   * The follower's own `error`, which is the one thing that can go wrong here
   * that the model knows nothing about: a recogniser that is fully downloaded and
   * still refuses to construct for a particular script. Passed in rather than
   * drawn beside this, because the two would otherwise both speak in the states
   * where the follower's error *is* the model's — `followerFailureMessage`
   * wrapped around the same sentence — and a strip of chrome has room for one.
   */
  otherwise,
}: {
  otherwise?: string;
}): JSX.Element | null {
  const offer = modelOffer(useModelStatus());
  if (offer === null) {
    if (otherwise === undefined) return null;
    return (
      <span className="web-chrome-error" role="alert">
        {otherwise}
      </span>
    );
  }

  return (
    <span className="web-model-chip" role="status">
      <span className="web-model-chip-says">{offer.says}</span>
      <Press action={offer.action} />
    </span>
  );
}

/** One of {@link ModelOffer}'s buttons, or nothing where the phase has none. A
 * component rather than a conditional in each place, so the two surfaces cannot
 * disagree about which command a label sends. */
function Press({
  action,
  className,
}: {
  action?: ModelAction;
  className?: string;
}): JSX.Element | null {
  if (action === undefined) return null;
  return (
    <button
      type="button"
      {...(className === undefined ? {} : { className })}
      onClick={() => modelCommand(action.command)}
    >
      {action.label}
    </button>
  );
}
